import { IReqCandidatesListItem } from 'modules/hiring/models/IRequisitionCandidates.interface';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import LabelValuePair from 'modules/hiring/components/labelValuePair';
import Widget from 'shared/components/Widget';
import { fullLongDateFormat } from 'utilities/formatDate';
import styles from './candidateProfile.module.scss';
import useCandidateContactDetails from 'modules/hiring/hooks/useCandidateContactDetails';
import { KFShimmerSkeleton, useTokenClaims } from '@kf/ic2fx';
import MakeDecisionModal from 'modules/hiring/components/decisionModal';
import Tabs, { TabItem } from 'shared/components/Tabs';
import CandidateApplicationDetails from 'modules/hiring/components/candidateApplication/candidateApplicationDetails';
import CandidateActivityHistory from 'modules/hiring/components/candidateApplication/candidateActivityHistory';
import {
  HMDecisionModalActions,
  TMakeDecisionStatus,
} from 'modules/hiring/models/hiringDecisionOption';
import {
  HiringActivity,
  HMStatusListForInterview,
  HMStatusListForScreen,
} from 'modules/hiring/models/candidatePipeline.model';
import CandidateResume from 'modules/hiring/components/CandidateResume';
import { ICandidateProfileApplicationHistory } from 'modules/hiring/models/candidateProfile.model';
import HiringBreadCrumb from 'modules/hiring/components/hiringBreadCrumb';
import IStoreState from 'store/IStoreState';
import { connect } from 'react-redux';
import {
  customEventSubscribe,
  customEventUnsubscribe,
} from 'modules/hiring/utilities';
import {
  axiosCandidatesApplication,
  axiosGetAssessmentStatus,
  axiosMakeHiringDecision,
} from 'modules/hiring/stateManagement/api';
import CandidateInterviewFeedback from 'modules/hiring/components/candidateInterviewFeedback';
import { useCandidateOverallRating } from 'modules/hiring/hooks/useCandidateOverallRating';
import { useInterviewFeedbackList } from 'modules/hiring/hooks/useInterviewFeedbackList';
import CandidateFeedbackList from 'modules/hiring/components/candidateInterviewFeedback/candidateFeedbackList';
import { getDecisionTag } from 'modules/hiring/components/decisionTag';
import ThumbsUpDownWidget from 'modules/hiring/components/thumsupdown';
import { mapDataForDecision } from 'modules/hiring/components/decisionModal/decisionModal.helper';
import { Canceler } from 'axios';
import OfferAndRejectButtons from 'modules/hiring/components/offerAndRejectButton';
import DecisionConfirmationModal from 'modules/hiring/components/decisionModal/decisionConfirmationModal';
import CandidateApplicationProfileDetails from 'modules/hiring/components/candidateApplication/candidateApplicationProfileDetails';
import { useConfig } from 'utilities/hooks/useConfig';
import LoaderSmall from 'modules/hiring/components/LoaderSmall';
import { REQUISITION_STATUS, ADAPTIVE_CARD_LABEL } from 'utilities/constants';
import { useTranslation } from 'react-i18next';

type TCandidatePipelinePageStateProps = ReturnType<typeof mapStateToProps>;

const CandidateProfilePage: React.FC<TCandidatePipelinePageStateProps> = ({
  currentRequisition,
}) => {
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  let currentCandidate: IReqCandidatesListItem = location?.state?.candidate
    ? location?.state?.candidate
    : {
        candidateID: Number(params?.get('candidateId')),
        requisitionID: Number(params?.get('requisitionId')),
        applicationID: Number(params?.get('applicationId')),
        name: params?.get('candidateName'),
        adaptiveCardLabel: params?.get('label'),
      };
  const { contactDetailsData } = useCandidateContactDetails(
    currentCandidate?.candidateID
  );
  const { t: translate } = useTranslation();
  const [loading, setIsLoading] = useState<boolean>(false);

  const [decisionLoader, setDecisionLoader] = useState<boolean>(false);
  const [data, setApplicationData] = useState<any>([]);
  const [note, setNote] = useState<string>('');
  const [completed, setCompleted] = useState<boolean>(false);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [notStarted, setNotStarted] = useState<boolean>(false);
  const [notInvited, setNotInvited] = useState<boolean>(false);
  const [isLoadingForStatus, setIsLoadingForStatus] = useState<boolean>(false);
  const [empty, setEmpty] = useState<boolean>(false);
  const { name: hiringManagerName } = useTokenClaims();
  const { config } = useConfig();

  let makeHiringDecisionCanceler: Canceler;

  function statusEmpty() {
    setEmpty(true);
    setNotStarted(false);
    setNotInvited(false);
    setInProgress(false);
    setCompleted(false);
  }

  const getCandidateApplicationData = async () => {
    setIsLoading(true);
    const response = await axiosCandidatesApplication(
      currentCandidate?.candidateID,
      currentCandidate?.requisitionID
    );
    /* istanbul ignore else */
    if (+response?.statusCode === 200) {
      setApplicationData(response?.applicationHistory);
      if (
        currentCandidate?.adaptiveCardLabel ===
        ADAPTIVE_CARD_LABEL.SCREENING_FEEDBACK
      ) {
        setCurrentTab('1');
        currentCandidate = {
          ...currentCandidate,
          adaptiveCardLabel: null,
        };
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    //call api to get the assessment status
    const getAssessment = async () => {
      try {
        if (
          config?.kfasClientId &&
          config?.isKfasEnabled &&
          data[0]?.participantId &&
          !data[0]?.isParticipantDeleted
        ) {
          setIsLoadingForStatus(true);

          const responseStatus: any = await axiosGetAssessmentStatus(
            config?.kfasClientId,
            data[0]?.participantId,
            data[0]?.kfasProjectId
          );
          if (responseStatus?.statusCode === 200) {
            if (responseStatus?.assessmentStatus === 'NOT_STARTED') {
              setNotStarted(true);
              setNotInvited(false);
              setInProgress(false);
              setCompleted(false);
              setEmpty(false);
            } else if (responseStatus?.assessmentStatus === 'NOT_INVITED') {
              setNotStarted(false);
              setNotInvited(true);
              setInProgress(false);
              setCompleted(false);
              setEmpty(false);
            } else if (responseStatus?.assessmentStatus === 'IN_PROGRESS') {
              //check weather status is reject then don't show status
              if (
                data[0]?.activityStatus === REQUISITION_STATUS.REJECT ||
                data[0]?.activityStatus === REQUISITION_STATUS.OPT_OUT_REJECT ||
                data[0]?.activityStatus ===
                  REQUISITION_STATUS.REJECT_CANDIDATE_WITH_FEEDBACK
              ) {
                statusEmpty();
              } else {
                setNotStarted(false);
                setNotInvited(false);
                setInProgress(true);
                setCompleted(false);
                setEmpty(false);
              }
            } else if (responseStatus?.assessmentStatus === 'COMPLETED') {
              setNotStarted(false);
              setNotInvited(false);
              setInProgress(false);
              setCompleted(true);
              setEmpty(false);
            } else {
              statusEmpty();
            }
          } else {
            statusEmpty();
          }

          setIsLoadingForStatus(false);
        }
      } catch (error) {
        throw new Error(`API Error: "${error}"`);
      }
    };

    getAssessment();
  }, [config, data]);

  const { aggregateData, loading: showLoading } = useCandidateOverallRating({
    requisitionID: Number(currentCandidate?.requisitionID),
    candidateID: currentCandidate?.candidateID,
  });

  const currentApplication: ICandidateProfileApplicationHistory = data.find(
    (item: ICandidateProfileApplicationHistory) =>
      item.applicationID === currentCandidate.applicationID
  );

  const [currentTab, setCurrentTab] = useState('0');
  const [showMakeDecisionModal, setShowMakeDecisionModal] =
    useState<boolean>(false);
  const [showDecisionModal, setShowDecisionModal] = useState<boolean>(false);
  const [decision, setDecision] = useState('');
  const candidateTabsConfig: TabItem[] = [
    {
      i18nKey: 'candidateDetails',
      component: (
        <CandidateApplicationProfileDetails
          data={currentApplication}
          contactData={contactDetailsData}
          candidateId={currentCandidate.candidateID}
          loading={loading}
        />
      ),
    },
    {
      i18nKey: 'screeningFeedback',
      component: (
        <CandidateInterviewFeedback
          aggregateData={aggregateData}
          loading={showLoading}
        />
      ),
    },
    {
      i18nKey: 'application',
      component: (
        <CandidateApplicationDetails
          data={currentApplication}
          loading={loading}
        />
      ),
    },
    {
      i18nKey: 'activity',
      component: (
        <CandidateActivityHistory data={currentApplication} loading={loading} />
      ),
    },
  ];

  const filteredTabsConfig = aggregateData?.agreegateResponse?.responseItemList
    ?.length
    ? candidateTabsConfig
    : candidateTabsConfig.filter((_, index) => index !== 1);

  const handleTabChange = (_: any, newValue: string) => {
    setCurrentTab(newValue);
  };

  const hasDataToMakeDecision =
    data?.length &&
    currentApplication?.jobTitle &&
    currentApplication?.requisitionID != null &&
    currentApplication?.jobTitle;

  const { feedbackList, isLoading } = useInterviewFeedbackList({
    requisitionID: Number(currentCandidate?.requisitionID),
    candidateID: currentCandidate?.candidateID,
  });

  const breadCrumbItems = [
    { title: translate('hiring'), path: '../overview' },
    {
      title:
        (currentRequisition?.jobTitle &&
          `${currentRequisition?.jobTitle} ${currentRequisition?.requisitionID}`) ||
        translate('candidatePipeline'),
      path: '../candidatePipeline',
    },
  ];

  useEffect(() => {
    getCandidateApplicationData();
    /* istanbul ignore else */
    customEventSubscribe('makeDecision', makeDecisionSuccess);
    // cleans up the adaptive cards - session storage
    sessionStorage.removeItem('hasLoaded');
    return () => {
      customEventUnsubscribe('makeDecision', makeDecisionSuccess);
    };
  }, []);

  const makeDecisionSuccess = () => {
    getCandidateApplicationData();
  };

  const handleClose = () => {
    setShowDecisionModal(false);
    setDecision('');
  };

  useEffect(() => {
    if (decision === 'Offer') {
      handleMakeDecision(HMDecisionModalActions.Offer);
    } else if (decision === 'Reject') {
      handleMakeDecision(HMDecisionModalActions.Pass);
    } else {
      // other decision
    }
  }, [decision]);

  const handleMakeDecision = async (activeDecision: HMDecisionModalActions) => {
    /* istanbul ignore else */
    if (activeDecision) {
      const payload = mapDataForDecision(
        currentApplication,
        note,
        activeDecision,
        hiringManagerName + ''
      );
      setDecisionLoader(true);
      const response = await axiosMakeHiringDecision(
        payload,
        (cancel: Canceler) => (makeHiringDecisionCanceler = cancel)
      );
      /* istanbul ignore else */
      if (response?.statusCode === 200) {
        makeDecisionSuccess();
      }
      setDecisionLoader(false);
      setShowDecisionModal(false);
    }
  };

  const getCandidateAction = (
    candidateCurrentApplication: ICandidateProfileApplicationHistory
  ) => {
    if (
      candidateCurrentApplication?.applicationStage ===
        HiringActivity.InScreen ||
      candidateCurrentApplication?.applicationStage === HiringActivity.Submit
    ) {
      if (
        candidateCurrentApplication?.activityStatus ===
        HMStatusListForScreen.Reject
      ) {
        return <></>;
      }
      return (
        <ThumbsUpDownWidget
          thumbsDownAction={() =>
            handleMakeDecision(HMDecisionModalActions.Pass)
          }
          thumbsUpAction={() =>
            handleMakeDecision(HMDecisionModalActions.Advance)
          }
        />
      );
    }
    if (
      candidateCurrentApplication?.applicationStage ===
        HiringActivity.InInterview ||
      candidateCurrentApplication?.applicationStage === HiringActivity.Interview
    ) {
      if (
        candidateCurrentApplication?.activityStatus ===
          HMStatusListForInterview.Reject ||
        candidateCurrentApplication?.activityStatus ===
          HMStatusListForInterview.RejectProcess2
      ) {
        return <></>;
      }
      return (
        <OfferAndRejectButtons
          offer={() => {
            // setShowDecisionModal(true);
            setDecision('Offer');
          }}
          reject={() => {
            // setShowDecisionModal(true);
            setDecision('Reject');
          }}
        />
      );
    }
    return <></>;
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <HiringBreadCrumb
          id='candidate-pipeline-breadcrumb'
          breadCrumbItems={breadCrumbItems}
        />
      </Grid>
      <Grid item xs={12}>
        <Stack id='candidate-profile-container' gap='1.25rem'>
          <Stack
            id='name-makedecision-container'
            className={styles.candidate_profile_header}
          >
            <Stack id='candidate-name-updated-date-container'>
              <div className={styles.job_group}>
                <Typography
                  id='candidate-name'
                  data-testid='candidate-name'
                  className={`kfic2-panelTitleBold ${styles.candidate_name}`}
                >
                  {currentCandidate?.name}
                </Typography>
                {isLoadingForStatus && (
                  <Box className={styles.showLoader}>
                    <LoaderSmall widgetHeight={20} />
                  </Box>
                )}
                {!isLoadingForStatus &&
                  config?.isKfasEnabled &&
                  config?.kfasClientId && (
                    <Chip
                      data-testid={'assessmentStatusChip'}
                      label={`
                    ${
                      completed
                        ? `${translate('completedText')}`
                        : inProgress
                        ? `${translate('inProgressText')}`
                        : notStarted
                        ? `${translate('notStartedText')}`
                        : notInvited
                        ? `${translate('notInvitedText')}`
                        : empty
                        ? ''
                        : ''
                    }`}
                      className={`
                    ${styles.assessment_status}
                    ${completed ? styles.completed : ''}
                    ${inProgress ? styles.in_progress : ''}
                    ${notStarted || notInvited ? styles.not_started : ''}
                  `}
                    />
                  )}
              </div>

              <LabelValuePair
                id='lastUpdated-date'
                label='lastUpdated'
                value={
                  contactDetailsData &&
                  fullLongDateFormat(contactDetailsData?.lastUpdatedDate ?? '')
                }
                valueSx={{
                  fontWeight: '400 !important',
                }}
              />
            </Stack>
            <Stack className={styles.action}>
              {!!data?.length ? (
                currentApplication?.currentActivityStatus ? (
                  getDecisionTag(
                    currentApplication?.applicationStage as HiringActivity,
                    currentApplication?.currentActivityStatus as TMakeDecisionStatus
                  )
                ) : decisionLoader || loading ? (
                  <KFShimmerSkeleton
                    data-testid='advance-reject-loader'
                    sx={{
                      width: '5.4rem',
                      height: '2.5rem',
                      background: 'white',
                      borderRadius: '1.25rem',
                    }}
                  />
                ) : (
                  getCandidateAction(currentApplication)
                )
              ) : (
                <></>
              )}
            </Stack>
          </Stack>
          <Stack id='candidate-profile-content-container'>
            <Stack
              id='candidate-profile-application-activity-container'
              gap='1.25rem'
              style={{
                width: 'calc(100% - 0px)',
              }}
            >
              <Widget
                id='candidate-profile-application-activity-wrapper'
                sx={{
                  borderRadius: ({ typography }) => typography.pxToRem(8),
                }}
              >
                <Tabs
                  aria-label={currentTab}
                  currentTab={currentTab.toString()}
                  setCurrentTab={handleTabChange}
                  tabs={filteredTabsConfig}
                  tabPanelSx={{
                    p: 0,
                  }}
                  muiTabIndicator={{
                    maxWidth: '100%',
                    height: '0.1875rem',
                    borderRadius: '6.25rem',
                  }}
                  sectionName='candidate-profile'
                  tabPanelWidth={{ width: '100%' }}
                  tabListItemSx={{
                    marginRight: '2.5rem',
                    '&.MuiButtonBase-root': {
                      '& p': {
                        fontWeight: '400',
                        color: '#757575',
                        padding: '8px 0',
                      },
                    },
                    '&.Mui-selected': {
                      '& p': {
                        fontWeight: '700',
                        color: '#008369',
                        padding: '8px 0',
                      },
                    },
                  }}
                ></Tabs>
              </Widget>
              {currentTab === '1' &&
                !!feedbackList?.individualResponseModelList?.length && (
                  <Widget
                    id='feedback-details-container'
                    data-testid='feedback-details-container'
                    sx={{
                      borderRadius: ({ typography }) => typography.pxToRem(8),
                      background: '#F8F9FB',
                    }}
                  >
                    <CandidateFeedbackList
                      feedbackList={feedbackList}
                      showLoading={isLoading}
                    />
                  </Widget>
                )}
            </Stack>
          </Stack>
          {!!showDecisionModal && (
            <DecisionConfirmationModal
              data={data}
              note={note}
              setNote={setNote}
              decision={decision}
              open={showDecisionModal}
              handleClose={handleClose}
              handleMakeDecision={handleMakeDecision}
              decisionLoader={decisionLoader}
              id='candidate-profile-offer-decision-modal'
            />
          )}
          {!!hasDataToMakeDecision && (
            <MakeDecisionModal
              data={{
                candidateId: currentCandidate.candidateID,
                jobTitle: currentApplication.jobTitle,
                name: currentCandidate.name,
                requisitionId: currentApplication.requisitionID,
              }}
              id='candidate-profile-make-decision-modal'
              open={showMakeDecisionModal}
              setOpen={setShowMakeDecisionModal}
            />
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state: IStoreState) => ({
  currentRequisition: state.selectedOpenRequisition,
});

export default connect(mapStateToProps)(CandidateProfilePage);
